import { GridColDef } from '@mui/x-data-grid';

import {
  Counterparty,
  CounterpartyQueryFilters,
  CounterpartySortColumns,
  CounterpartyType,
  Iban
} from 'openapi';

import {
  ACTIONS,
  APPROVED,
  COLUMNS_DEFAULT_OPTIONS,
  DEFAULT_PAYMENT,
  EMPTY_CELL_VALUE,
  TYPE
} from 'utils/constants/constants';
import {
  COMPANY_FILTER_FIELDS,
  COMPANIES_FILTERS_DEFAULT_VALUES,
  CounterpartyFilterField,
  CounterpartyFilterFormValues
} from 'utils/constants/counterparty';
import { TranslateFunction } from 'utils/interfaces/Translations';

export interface CounterpartyFilterConfig {
  fields: CounterpartyFilterField[];
  defaultValues: CounterpartyFilterFormValues;
}

const COUNTERPARTY_TYPE_TRANSLATION_KEYS: Record<CounterpartyType, string> = {
  [CounterpartyType.SUPPLIER]: 'labels.company',
  [CounterpartyType.REIMBURSEMENT]: 'labels.reimbursement'
};

const getEmptyCellValue = (value: unknown): unknown =>
  value === null || value === undefined || value === ''
    ? EMPTY_CELL_VALUE
    : value;

export const getDefaultPaymentIban = (ibans?: Iban[]): string | undefined => {
  if (!ibans?.length) {
    return undefined;
  }

  const sortedIbans = [...ibans].sort(
    (first, second) => (first.sortOrder ?? 0) - (second.sortOrder ?? 0)
  );
  const primaryIban =
    sortedIbans.find((iban) => iban.isSelectedForPayment) ?? sortedIbans[0];

  return primaryIban?.iban || undefined;
};

export const getCounterpartyTypeLabel = (
  type: CounterpartyType | undefined,
  translate: TranslateFunction
): string => {
  const translationKey = type && COUNTERPARTY_TYPE_TRANSLATION_KEYS[type];

  return translationKey ? translate(translationKey) : '';
};

export const isReimbursementCounterparty = (
  counterparty: Pick<Counterparty, 'type'>
): boolean => counterparty.type === CounterpartyType.REIMBURSEMENT;

export const getCounterpartyRowId = (
  counterparty: Pick<Counterparty, 'companyCounterpartyId' | 'id'>
): number => counterparty.companyCounterpartyId ?? counterparty.id ?? 0;

const getRegistrationNumberValue = (
  _value: unknown,
  row: Counterparty
): string =>
  isReimbursementCounterparty(row)
    ? EMPTY_CELL_VALUE
    : (getEmptyCellValue(row.registrationNumber) as string);

const getVatNumberValue = (_value: unknown, row: Counterparty): string =>
  isReimbursementCounterparty(row)
    ? EMPTY_CELL_VALUE
    : (getEmptyCellValue(row.vatNumber) as string);

export const getCounterpartyColumns = (
  translate: TranslateFunction
): GridColDef[] => [
  {
    ...COLUMNS_DEFAULT_OPTIONS,
    field: CounterpartySortColumns.NAME,
    headerName: translate('labels.name'),
    minWidth: 250,
    valueFormatter: (value: unknown) => getEmptyCellValue(value) as string
  },
  {
    ...COLUMNS_DEFAULT_OPTIONS,
    field: TYPE,
    headerName: translate('labels.type'),
    minWidth: 150,
    maxWidth: 200,
    sortable: false,
    resizable: false
  },
  {
    ...COLUMNS_DEFAULT_OPTIONS,
    field: APPROVED,
    headerName: translate('labels.approved'),
    minWidth: 160,
    maxWidth: 200,
    display: 'flex',
    sortable: false,
    resizable: false
  },
  {
    ...COLUMNS_DEFAULT_OPTIONS,
    field: CounterpartySortColumns.REGISTRATION_NUMBER,
    headerName: translate('labels.companyRegistrationNumber'),
    minWidth: 200,
    maxWidth: 250,
    valueGetter: getRegistrationNumberValue
  },
  {
    ...COLUMNS_DEFAULT_OPTIONS,
    field: CounterpartySortColumns.VAT_NUMBER,
    headerName: translate('labels.vatNumber'),
    minWidth: 250,
    maxWidth: 300,
    valueGetter: getVatNumberValue
  },
  {
    ...COLUMNS_DEFAULT_OPTIONS,
    field: DEFAULT_PAYMENT,
    headerName: translate('labels.defaultPayment'),
    minWidth: 300,
    sortable: false
  },
  {
    ...COLUMNS_DEFAULT_OPTIONS,
    field: CounterpartySortColumns.EXPENSE_TYPE,
    headerName: translate('labels.expenseTypes'),
    valueGetter: (_value: any, row: any) =>
      (row.expenseTypes ?? [])
        .map((expenseType: { name?: string }) => expenseType?.name)
        .filter(Boolean)
        .join(', '),
    minWidth: 240,
    maxWidth: 400,
    display: 'flex',
    valueFormatter: (value: unknown) => getEmptyCellValue(value) as string
  },
  {
    ...COLUMNS_DEFAULT_OPTIONS,
    field: ACTIONS,
    headerName: '',
    flex: 0,
    width: 64,
    minWidth: 64,
    maxWidth: 64,
    align: 'center',
    headerAlign: 'center',
    sortable: false
  }
];

export const toCounterpartyQueryFilters = (
  values: CounterpartyFilterFormValues
): CounterpartyQueryFilters => {
  const expenseTypes = values.expenseTypes ?? [];
  const isReimbursement = values.type === CounterpartyType.REIMBURSEMENT;

  return {
    names: values.names ?? [],
    registrationNumbers: values.registrationNumbers ?? [],
    vatNumbers: values.vatNumbers ?? [],
    expenseTypes:
      isReimbursement || expenseTypes.length === 0 ? undefined : expenseTypes,
    types: values.type ? [values.type] : undefined,
    isValidated: values.approved ?? undefined
  };
};

export const getCounterpartyFilter = (): CounterpartyFilterConfig => ({
  fields: COMPANY_FILTER_FIELDS,
  defaultValues: COMPANIES_FILTERS_DEFAULT_VALUES
});
